import React, { useEffect, useState } from "react";
import {
    Box,
    Typography,
    Grid,
    TextField,
    Paper,
    Button,
    MenuItem,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress
} from "@mui/material";

import {
    collection,
    getDocs,
    query,
    where,
    orderBy,
    doc,
    getDoc
} from "firebase/firestore";

import { auth, db } from "../../firebase";

import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

function Studentreport() {

    const [schoolId, setSchoolId] = useState("");

    const [courses, setCourses] = useState([]);
    const [studenttypes, setStudenttypes] = useState([]);

    const [loading, setLoading] = useState(false);

    const [filters, setFilters] = useState({
        fromDate: "",
        toDate: "",
        class: "",
        gender: "",
        studenttype: ""
    });

    const [reports, setReports] = useState([]);

    // ================= FETCH SCHOOL ID =================

    useEffect(() => {

        const fetchUserSchool = async () => {

            try {

                const uid = auth.currentUser?.uid;

                if (!uid) return;

                const userRef = doc(db, "users", uid);

                const userSnap = await getDoc(userRef);

                if (userSnap.exists()) {

                    const userData = userSnap.data();

                    setSchoolId(userData.schoolId || "");

                }

            } catch (err) {
                console.log(err);
            }

        };

        fetchUserSchool();

    }, []);

    // ================= FETCH MASTER DATA =================

    useEffect(() => {

        if (!schoolId) return;

        const fetchMasters = async () => {

            try {

                // ================= COURSES =================

                const courseQuery = query(
                    collection(db, "courses"),
                    where("schoolId", "==", schoolId),
                    orderBy("sequence", "asc")
                );

                const courseSnap = await getDocs(courseQuery);

                setCourses(
                    courseSnap.docs.map(doc => ({
                        id: doc.id,
                        name: doc.data().name
                    }))
                );

                // ================= STUDENT TYPES =================

                const typeQuery = query(
                    collection(db, "studenttypes"),
                    where("schoolId", "==", schoolId)
                );

                const typeSnap = await getDocs(typeQuery);

                setStudenttypes(
                    typeSnap.docs.map(doc => ({
                        id: doc.id,
                        name: doc.data().type
                    }))
                );

            } catch (err) {
                console.log(err);
            }

        };

        fetchMasters();

    }, [schoolId]);

    // ================= HANDLE CHANGE =================

    const handleChange = (field, value) => {

        setFilters(prev => ({
            ...prev,
            [field]: value
        }));

    };
    const fetchReport = async () => {

        try {

            setLoading(true);

            // ================= ADMISSION DATA =================

            const admissionQuery = query(
                collection(db, "admission"),
                where("schoolId", "==", schoolId)
            );

            const admissionSnap = await getDocs(admissionQuery);

            let admissionData = admissionSnap.docs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));

            // ================= FILTERS =================

            // CLASS FILTER
            if (filters.class) {

                admissionData = admissionData.filter(
                    item => item.class === filters.class
                );

            }

            // STUDENT TYPE FILTER
            if (filters.studenttype) {

                admissionData = admissionData.filter(
                    item =>
                        (item.studenttype || "") === filters.studenttype
                );

            }

            // GENDER FILTER
            if (filters.gender) {

                admissionData = admissionData.filter(item => {

                    const gender =
                        (item.gender || "")
                            .toString()
                            .toUpperCase();

                    return (
                        gender === filters.gender.toUpperCase()
                    );

                });

            }

            // DATE FILTER
            if (filters.fromDate && filters.toDate) {

                const from = new Date(filters.fromDate);
                from.setHours(0, 0, 0, 0);

                const to = new Date(filters.toDate);
                to.setHours(23, 59, 59, 999);

                admissionData = admissionData.filter(item => {

                    let createdAt = item.createdAt;

                    if (createdAt?.toDate) {
                        createdAt = createdAt.toDate();
                    } else {
                        createdAt = new Date(createdAt);
                    }

                    return createdAt >= from && createdAt <= to;

                });

            }

            // ================= FETCH ALL STUDENTS PARALLEL =================

            const studentPromises = admissionData.map(async (item) => {

                try {

                    const studentId = item.studentId || item.id;

                    const studentRef = doc(db, "students", studentId);

                    const studentSnap = await getDoc(studentRef);

                    if (!studentSnap.exists()) return null;

                    const student = studentSnap.data();

                    // ================= CLASS NAME =================

                    let className = "";

                    const classId = item.class || student.class;

                    // GET CLASS NAME FROM ALREADY LOADED COURSES
                    const classObj = courses.find(
                        c => c.id === classId
                    );

                    if (classObj) {
                        className = classObj.name;
                    }

                    return {

                        id: studentId,

                        rollno: student.rollno || "",
                        admno: student.admno || "",

                        firstname: student.firstname || "",
                        fathername: student.fathername || "",
                        surname: student.surname || "",

                        gender:
                            student.gender ||
                            "",

                        class: classId || "",

                        classname: className || "",

                        studenttype:
                            student.studenttype ||
                            item.studenttype ||
                            "",

                        mobile: student.mobile || "",

                        createdAt:
                            student.createdAt ||
                            item.createdAt

                    };

                } catch (err) {

                    console.log(err);

                    return null;

                }

            });

            // ================= WAIT ALL =================

            const finalData = await Promise.all(studentPromises);

            // REMOVE NULLS
            const filteredData = finalData.filter(Boolean);

            setReports(filteredData);

            setLoading(false);

        } catch (err) {

            console.log(err);

            setLoading(false);

        }

    };
    // ================= PDF =================

    const downloadPDF = () => {

        const pdf = new jsPDF();

        pdf.setFontSize(16);

        pdf.text("Student Report", 14, 15);

        autoTable(pdf, {

            startY: 25,

            head: [[
                "Sr No",
                "Roll No",
                "Admission No",
                "Student Name",
                "Father Name",
                "Surname",
                "Gender",
                "Class",
                "Admission Type"
            ]],

            body: reports.map((row, index) => ([

                index + 1,
                row.rollno,
                row.admno,
                row.firstname,
                row.fathername,
                row.surname,
                row.gender,
                row.className,
                row.studenttype

            ]))

        });

        pdf.save("student-report.pdf");

    };

    return (

        <Box p={1}>

            <Paper sx={{ p: 2 }}>

                {/* HEADER */}

                <Box
                    sx={{
                        background: "#0e6363",
                        color: "#fff",
                        p: 1.5,
                        mb: 3
                    }}
                >
                    <Typography fontWeight="bold" fontSize={18}>
                        Student Report
                    </Typography>
                </Box>

                {/* FILTERS */}

                <Grid className="row" spacing={2}>

                    {/* FROM DATE */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="From Date"
                            InputLabelProps={{ shrink: true }}
                            value={filters.fromDate}
                            onChange={(e) =>
                                handleChange("fromDate", e.target.value)
                            }
                        />

                    </Grid>

                    {/* TO DATE */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <TextField
                            fullWidth
                            type="date"
                            size="small"
                            label="To Date"
                            InputLabelProps={{ shrink: true }}
                            value={filters.toDate}
                            onChange={(e) =>
                                handleChange("toDate", e.target.value)
                            }
                        />

                    </Grid>

                    {/* CLASS */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Class"
                            value={filters.class}
                            onChange={(e) =>
                                handleChange("class", e.target.value)
                            }
                        >

                            <MenuItem value="">
                                All
                            </MenuItem>

                            {courses.map((item) => (

                                <MenuItem
                                    key={item.id}
                                    value={item.id}
                                >
                                    {item.name}
                                </MenuItem>

                            ))}

                        </TextField>

                    </Grid>

                    {/* GENDER */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Gender"
                            value={filters.gender}
                            onChange={(e) =>
                                handleChange("gender", e.target.value)
                            }
                        >

                            <MenuItem value="">
                                All
                            </MenuItem>

                            <MenuItem value="MALE">
                                MALE
                            </MenuItem>

                            <MenuItem value="FEMALE">
                                FEMALE
                            </MenuItem>

                        </TextField>

                    </Grid>

                    {/* STUDENT TYPE */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <TextField
                            select
                            fullWidth
                            size="small"
                            label="Admission Type"
                            value={filters.studenttype}
                            onChange={(e) =>
                                handleChange("studenttype", e.target.value)
                            }
                        >

                            <MenuItem value="">
                                All
                            </MenuItem>

                            {studenttypes.map((item) => (

                                <MenuItem
                                    key={item.id}
                                    value={item.name}
                                >
                                    {item.name}
                                </MenuItem>

                            ))}

                        </TextField>

                    </Grid>

                    {/* BUTTONS */}

                    <Grid className="col-sm-2 mb-2" item xs={12} sm={2}>

                        <Box display="flex" gap={1}>

                            <Button
                                fullWidth
                                variant="contained"
                                onClick={fetchReport}
                            >
                                Preview
                            </Button>

                            <Button
                                fullWidth
                                variant="contained"
                                color="success"
                                onClick={downloadPDF}
                                disabled={reports.length === 0}
                            >
                                PDF
                            </Button>

                        </Box>

                    </Grid>

                </Grid>

                {/* TABLE */}

                <Box mt={4}>

                    {loading ? (

                        <Box textAlign="center">
                            <CircularProgress />
                        </Box>

                    ) : (

                        <TableContainer component={Paper}>

                            <Table size="small">

                                <TableHead>

                                    <TableRow>

                                        <TableCell>Sr No</TableCell>
                                        <TableCell>Roll No</TableCell>
                                        <TableCell>Admission No</TableCell>
                                        <TableCell>Student Name</TableCell>
                                        <TableCell>Gender</TableCell>
                                        <TableCell>Class</TableCell>
                                        <TableCell>Admission Type</TableCell>

                                    </TableRow>

                                </TableHead>

                                <TableBody>

                                    {reports.length > 0 ? (

                                        reports.map((row, index) => (

                                            <TableRow key={index}>

                                                <TableCell>{index + 1}</TableCell>
                                                <TableCell>{row.rollno}</TableCell>
                                                <TableCell>{row.admno}</TableCell>
                                                <TableCell style={{ textTransform: "uppercase" }}>{row.firstname} {row.fathername} {row.surname}</TableCell>
                                                <TableCell>{row.gender}</TableCell>
                                                <TableCell>{row.classname}</TableCell>
                                                <TableCell>{row.studenttype}</TableCell>

                                            </TableRow>

                                        ))

                                    ) : (

                                        <TableRow>

                                            <TableCell colSpan={9} align="center">
                                                No Data Found
                                            </TableCell>

                                        </TableRow>

                                    )}

                                </TableBody>

                            </Table>

                        </TableContainer>

                    )}

                </Box>

            </Paper>

        </Box>

    );
}

export default Studentreport;